import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import useRateLimitedMutation, { RATE_LIMIT_WINDOW_MS } from '@/app/mutations/Shared/useRateLimitedMutation';
import { RecordingContext, Recording } from '@/app/mutations/RecordingMutation/context';
import { StopOptions } from '@/app/mutations/Shared/types';
import fetchRecording, { FetchRecordingParams } from '@/app/mutations/RecordingMutation/fetchRecording';
import { isRateLimitError } from '@/app/utils/errors';
import { useToast } from '@/components/Toast/context';
import { useSentry } from '@/sentry/context';

export default function withRecordingMutation<T extends object>(
  Component: (props: T) => ReactNode,
) {
  return function ConnectedComponent(props: T) {
    const { hash } = useParams<{ hash: string }>();

    const Sentry = useSentry();

    const toast = useToast();

    const [isRecording, setIsRecording] = useState(false);

    const { error, mutate, status, variables } = useRateLimitedMutation({
      async mutationFn(params: { action: FetchRecordingParams['action'] } & StopOptions) {
        // `force` (if present) only gates the rate limiter — it is not sent to the server.
        await fetchRecording({ action: params.action, meeting_id: hash! })
      },
      // Per-mutation, so the outcome of each call is applied even if a later
      // mutate() has already replaced the observer's status. Deriving this from
      // `status` instead loses a successful start whenever a second start is
      // dispatched (and throttled) before the first resolves.
      onSuccess(_data, params) {
        setIsRecording(params.action === 'start');
      },
      retry: false,
    }, RATE_LIMIT_WINDOW_MS);

    // A start that has been dispatched but not yet resolved. The gate needs this
    // separately from `isRecording`: that flag only flips once the server call
    // returns, so without it a presence blip in the meantime re-fires start(),
    // the duplicate is throttled away, and the successful first start's result is
    // lost — leaving the client believing it is not recording while the server is.
    const isStartPending = status === 'pending' && variables?.action === 'start';

    const recording = useMemo<Recording>(() => ({
      isRecording,
      isStartPending,
      start: () => mutate({ action: 'start' }),
      stop: (options?: StopOptions) => mutate({ action: 'stop', force: options?.force }),
    }), [isRecording, isStartPending, mutate]);

    useEffect(function onMutationStatusChange() {
      switch (status) {
        case 'idle':
        case 'pending':
          break;
        case 'success':
          // isRecording is set in onSuccess, per mutation.
          break;
        case 'error':
          if (isRateLimitError(error)) {
            console.warn(error.message);
          } else {
            toast.publish({ text: `Recording ${variables?.action} failed: Please refresh page.`, variant: 'destructive' });
            
            // Trigger Sentry for recording failures
            if (Sentry) {
              Sentry.captureException(error, {
                tags: {
                  operation: 'recording',
                  action: variables?.action,
                },
                extra: {
                  meetingId: hash,
                  timestamp: new Date().toISOString(),
                }
              });
            }
          }
          break;
        default: {
          const neverType: never = status;
          throw new Error(`Unhandled status "${neverType}" in withRecordingMutation`);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, status, toast, variables?.action]);

    return (
      <RecordingContext.Provider value={recording}>
        <Component {...props} />
      </RecordingContext.Provider>
    );
  };
}
